import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatTabsModule } from '@angular/material/tabs';
import { MatMenuModule } from '@angular/material/menu';
import { MatDialog } from '@angular/material/dialog';
import { PhoneNumberPipe } from '@shared/pipes/phone-number.pipe';
import { Observable, of } from 'rxjs';
import { catchError, switchMap, map, startWith, takeUntil } from 'rxjs/operators';
import { Subject, combineLatest } from 'rxjs';
import { Person } from '../models/person.model';
import { PersonCommunityHistoryItem } from '../models/person-community-history.model';
import { PersonEvent, PersonEventCategory, personEventCategoryOptions } from '../models/person-event.model';
import { PersonDivineServiceAttendance, PersonService, PersonFamilyRelationships, ResponsiblePriestMember } from '../services/person.service';
import { PersonEventService } from '../services/person-event.service';
import { DetailCardComponent } from '@shared/components/detail-card/detail-card.component';
import { DashboardTimelineItem } from '@shared/components/dashboard-cards';
import { MatPaginatorModule } from '@angular/material/paginator';
import { MemberReferenceComponent } from '@shared/components/member-reference/member-reference.component';
import { OrganizationUnitItem } from '@features/user/models/filter-data.model';
import { BreadcrumbService } from '@core/services/breadcrumb.service';
import {
  documentTypeLabels,
  genderLabels,
  relationshipSituationLabels,
  RelationshipSituation,
} from '@core/api/enums';
import { DocumentType, Gender, MinistryStatus } from '@core/api/generated/api-client';
import { MinistryStatusBadgeComponent } from '@shared/components/ministry-status-badge/ministry-status-badge.component';
import { MembershipStatusBadgeComponent } from '@shared/components/membership-status-badge/membership-status-badge.component';
import { OrganizationUnitApiService } from '@core/api/services/organization-unit-api.service';
import { loadCommunityHierarchy } from './person-community-hierarchy';
import { buildPersonMinistryActivityLines } from './person-ministry-activity-lines';
import { UserFullInfoService } from '@core/services/user-full-info.service';
import { ResponsiblePriestDialogComponent } from '../components/responsible-priest-dialog.component';
import { SpiritualAssistanceVisit, SpiritualAssistanceVisitApiService } from '@core/api/services/spiritual-assistance-visit-api.service';
import { DivineServiceApiService, DivineServiceListDto } from '@core/api/services/divine-service-api.service';
import { PersonProfileTabStateService } from './person-profile-tab-state.service';

interface CurrentActivityItem {
  name: string;
  unitId?: string;
  unitName?: string;
  unitLevelId?: number;
  detail?: string;
  statusLabel?: string;
  ministryStatus?: MinistryStatus;
  ministryActivityUnits?: { id: string; name: string }[];
  summaryUnits?: { id?: string; name: string }[];
  requiresActiveMinistry?: boolean;
  typeLabel: string;
}

interface CurrentActivityCategory {
  label: string;
  items: CurrentActivityItem[];
}

interface CurrentActivityGroup {
  unitId?: string;
  unitName: string;
  unitLevelId: number;
  categories: CurrentActivityCategory[];
}

interface MinisterialTimelineItem extends DashboardTimelineItem {
  areas: { id?: string; name: string }[];
}

interface PersonPageViewModel {
  person: Person;
  age?: number;
  activity: {
    groups: CurrentActivityGroup[];
    categories: CurrentActivityCategory[];
  };
  events: PersonEvent[];
  communityHistory: PersonCommunityHistoryItem[];
  communityAncestors: OrganizationUnitItem[];
  divineServiceAttendance: PersonDivineServiceAttendance[];
  recentDivineServices: DivineServiceListDto[];
  familyRelationships: PersonFamilyRelationships;
  trustedMembers: ResponsiblePriestMember[];
  spiritualAssistanceVisits: SpiritualAssistanceVisit[];
}

@Component({
  selector: 'app-person-home-page',
  standalone: true,
  imports: [
    CommonModule,
    RouterLink,
    MatIconModule,
    MatButtonModule,
    MatProgressSpinnerModule,
    MatTooltipModule,
    MatTabsModule,
    MatMenuModule,
    PhoneNumberPipe,
    DetailCardComponent,
    MinistryStatusBadgeComponent,
    MembershipStatusBadgeComponent,
    MatPaginatorModule,
    MemberReferenceComponent,
  ],
  templateUrl: './person-home-page.component.html',
  styleUrl: './person-home-page.component.scss'
})
export class PersonHomePageComponent implements OnInit {
  readonly vm$: Observable<PersonPageViewModel | null>;
  readonly canManageMemberData$;
  readonly filterOptions = personEventCategoryOptions;
  activeFilters: PersonEventCategory[] = ['Collaboration', 'MinisterialActivity', 'ReligiousEvent'];
  loadError: string | null = null;
  trustedMembersPageIndex = 0;
  private selectedDivineServiceId = '';

  private personId: string = '';
  private readonly expandedActivityTableGroups = new Set<string>();
  private readonly destroy$ = new Subject<void>();
  private readonly reload$ = new Subject<void>();

  constructor(
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly personService: PersonService,
    private readonly personEventService: PersonEventService,
    private readonly breadcrumbService: BreadcrumbService,
    private readonly dialog: MatDialog,
    private readonly spiritualAssistanceVisitApi: SpiritualAssistanceVisitApiService,
    private readonly divineServiceApi: DivineServiceApiService,
    private readonly organizationUnitApi: OrganizationUnitApiService,
    userAccess: UserFullInfoService,
    private readonly profileTabState: PersonProfileTabStateService,
  ) {
    this.vm$ = this.initializeViewModel();
    this.canManageMemberData$ = userAccess.canManageMemberData$;
  }

  ngOnInit(): void {}

  get selectedTabIndex(): number {
    return this.profileTabState.getSelectedTab(this.personId);
  }

  rememberSelectedTab(tabIndex: number): void {
    this.profileTabState.setSelectedTab(this.personId, tabIndex);
  }

  editPerson(): void {
    this.router.navigate([...this.getPersonBasePath(), 'edit']);
  }

  navigateToUnit(unitId: string): void {
    this.router.navigate(['/units', unitId]);
  }

  navigateToEvents(): void {
    this.router.navigate([...this.getPersonBasePath(), 'events']);
  }

  navigateToCommunityHistory(): void {
    this.router.navigate([...this.getPersonBasePath(), 'community-history']);
  }

  navigateToFamilyRelationships(): void { this.router.navigate([...this.getPersonBasePath(), 'family-relationships']); }
  manageResponsiblePriest(person: Person): void {
    this.dialog.open(ResponsiblePriestDialogComponent, { data: { personId: person.id, personName: this.getFullName(person), responsiblePriestId: person.responsiblePriestId } })
      .afterClosed().subscribe(changed => { if (changed) this.reloadPerson(); });
  }

  navigateToPerson(personId: string): void { this.router.navigate(['/people', personId]); }

  getRelationshipNames(items: { relatedPersonName: string }[]): string { return items.length ? items.map(x => x.relatedPersonName).join(', ') : '-'; }

  getFullName(person: Person): string {
    return `${person.firstName} ${person.lastName}`.trim() || this.getPersonalInfoValue();
  }

  getDocumentInfo(person: Person): string {
    const documentType = person.documentType
      ? documentTypeLabels[person.documentType as DocumentType] ?? person.documentType
      : '';
    const documentNumber = person.documentNumber?.trim() ?? '';
    const value = [documentType, documentNumber].filter(Boolean).join(' ');

    return this.getPersonalInfoValue(value);
  }

  getBirthDateInfo(person: Person, age?: number): string {
    if (!person.dateOfBirth) {
      return this.getPersonalInfoValue();
    }

    const date = person.dateOfBirth.toLocaleDateString('es-AR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
    const ageText = age !== undefined ? ` (${age} años)` : '';

    return `${date}${ageText}`;
  }

  getGenderLabel(person: Person): string {
    return this.getPersonalInfoValue(
      person.gender ? genderLabels[person.gender as Gender] ?? person.gender : undefined
    );
  }

  getRelationshipSituationLabel(person: Person): string {
    return this.getPersonalInfoValue(
      person.relationshipSituation
        ? relationshipSituationLabels[person.relationshipSituation as RelationshipSituation] ?? person.relationshipSituation
        : undefined
    );
  }

  getPersonalInfoValue(value?: string | null): string {
    const trimmedValue = value?.trim();
    return trimmedValue ? trimmedValue : '-';
  }

  getGoogleMapsUrl(person: Person): string {
    return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(person.address ?? '')}`;
  }

  getMailToUrl(person: Person): string {
    return `mailto:${person.email ?? ''}`;
  }

  private getPersonBasePath(): string[] {
    const allParams = this.route.snapshot.pathFromRoot.reduce(
      (acc, r) => ({ ...acc, ...r.params }), {} as Record<string, string>
    );
    return allParams['unit-id']
      ? ['/units', allParams['unit-id'], 'members', this.personId]
      : ['/people', this.personId];
  }

  reloadPerson(): void {
    this.trustedMembersPageIndex = 0;
    this.reload$.next();
  }

  toggleFilter(filterValue: PersonEventCategory): void {
    this.activeFilters = this.activeFilters.includes(filterValue)
      ? this.activeFilters.filter(value => value !== filterValue)
      : [...this.activeFilters, filterValue];
  }

  toggleTimelineFilter(filterValue: string): void {
    this.toggleFilter(filterValue as PersonEventCategory);
  }

  getTimelineItems(vm: PersonPageViewModel): DashboardTimelineItem[] {
    return vm.events.map(event => ({
      id: event.id,
      date: event.eventDate,
      title: this.getTimelineTitle(event),
      detail: this.getTimelineDetail(event),
      category: event.category,
      categoryLabel: event.categoryName,
      theme: this.getCategoryTheme(event.category)
    }));
  }

  getFilteredTimelineItems(vm: PersonPageViewModel): DashboardTimelineItem[] {
    return this.getTimelineItems(vm).filter(item => this.activeFilters.includes(item.category as PersonEventCategory));
  }

  getMinisterialTimelineItems(vm: PersonPageViewModel): MinisterialTimelineItem[] {
    return vm.events
      .filter(event => event.category === 'MinisterialActivity')
      .map(event => ({
        id: event.id,
        date: event.eventDate,
        title: this.getTimelineTitle(event),
        detail: this.getTimelineDetail(event),
        category: event.category,
        categoryLabel: event.categoryName,
        theme: this.getCategoryTheme(event.category),
        areas: this.getMinisterialEventAreas(event)
      }));
  }

  private getTimelineTitle(event: PersonEvent): string {
    return this.isOrdinationTimelineEvent(event) && event.detail ? event.detail : event.eventTypeName;
  }

  private getTimelineDetail(event: PersonEvent): string | undefined {
    if (!this.isOrdinationTimelineEvent(event)) {
      return event.detail;
    }

    return event.detail ? event.eventTypeName : undefined;
  }

  private isOrdinationTimelineEvent(event: PersonEvent): boolean {
    return event.eventKind === 'ministerial-activity' && event.eventTypeKey === 'Ordination';
  }

  private getMinisterialEventAreas(event: PersonEvent): MinisterialTimelineItem['areas'] {
    if (event.eventKind === 'ministerial-activity' && event.eventTypeKey === 'LeaveOfAbsence') {
      return [];
    }

    const areas = event.ministerialAreas ?? [];
    if (areas.length) {
      const ordinationAreas = event.eventKind === 'ministerial-activity' && event.eventTypeKey === 'Ordination'
        ? areas.filter(area => area.changeType === 'Added' && (area.kind === 'Appointment' || area.kind === 'Assignment'))
        : [];
      const displayedAreas = ordinationAreas.length ? ordinationAreas : areas;
      return [...new Map(displayedAreas.map(area => [
        area.organizationUnitId ?? `historical:${area.organizationUnitName}`,
        {
          id: area.isHistoricalOrganizationUnit ? undefined : area.organizationUnitId,
          name: area.organizationUnitName
        }
      ])).values()];
    }

    return event.organizationUnitName
      ? [{ id: event.organizationUnitId, name: event.organizationUnitName }]
      : [];
  }

  hasMinisterialActivity(vm: PersonPageViewModel): boolean {
    return this.getMinistryActivityLines(vm).length > 0 || this.getMinisterialTimelineItems(vm).length > 0;
  }

  getMinistryActivityLines(vm: PersonPageViewModel) {
    return buildPersonMinistryActivityLines(vm.person, vm.events);
  }

  getRecentCommunities(vm: PersonPageViewModel): PersonCommunityHistoryItem[] {
    return vm.communityHistory.slice(0, 3);
  }

  formatCommunityDate(item: PersonCommunityHistoryItem): string {
    if (!item.validFrom) {
      return 'Sin fecha';
    }

    const [year, month, day] = item.validFrom.split('-').map(Number);
    return new Date(year, month - 1, day).toLocaleDateString('es-AR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  }

  getCategoryTheme(category: PersonEventCategory): string {
    switch (category) {
      case 'Collaboration':
        return 'collaboration';
      case 'MinisterialActivity':
        return 'ministerial-activity';
      case 'ReligiousEvent':
        return 'religious-event';
      default:
        return 'activity';
    }
  }

  hasAnyActivity(vm: PersonPageViewModel): boolean {
    return vm.activity.groups.length > 0;
  }

  getActivityGroupKey(group: CurrentActivityGroup): string {
    return group.unitId || group.unitName;
  }

  getActivityGroupItemCount(group: CurrentActivityGroup): number {
    return group.categories.reduce((count, category) => count + category.items.length, 0);
  }

  getCollaborationActivityCategories(vm: PersonPageViewModel): CurrentActivityCategory[] {
    return vm.activity.categories.flatMap(category => {
      if (category.label === 'Colaboraciones') {
        return [category];
      }

      if (category.label === 'Nombramientos') {
        const items = category.items.filter(item => !item.requiresActiveMinistry);
        return items.length ? [{ ...category, items }] : [];
      }

      return [];
    });
  }

  getActivitySummaryItems(category: CurrentActivityCategory): CurrentActivityItem[] {
    if (category.label === 'Ministerio') {
      return category.items;
    }

    if (category.label === 'Colaboraciones') {
      const groupedByCollaborationAndUnit = new Map<string, CurrentActivityItem>();
      for (const item of category.items) {
        const unitKey = item.unitId || item.unitName || 'without-unit';
        const key = JSON.stringify([item.name, unitKey, item.statusLabel]);
        const current = groupedByCollaborationAndUnit.get(key);
        if (!current) {
          groupedByCollaborationAndUnit.set(key, {
            ...item,
            summaryUnits: item.unitName ? [{ id: item.unitId, name: item.unitName }] : []
          });
          continue;
        }

        const roles = new Set([current.detail, item.detail].filter((role): role is string => !!role));
        current.detail = [...roles].join(', ') || undefined;
      }

      return [...groupedByCollaborationAndUnit.values()];
    }

    const grouped = new Map<string, CurrentActivityItem>();
    for (const item of category.items) {
      const key = JSON.stringify([item.name, item.detail, item.statusLabel]);
      const current = grouped.get(key);
      if (!current) {
        grouped.set(key, { ...item, summaryUnits: item.unitName ? [{ id: item.unitId, name: item.unitName }] : [] });
        continue;
      }

      if (item.unitName && !current.summaryUnits?.some(unit => (unit.id || unit.name) === (item.unitId || item.unitName))) {
        current.summaryUnits?.push({ id: item.unitId, name: item.unitName });
      }
    }

    return [...grouped.values()];
  }

  canCollapseActivityGroup(group: CurrentActivityGroup): boolean {
    return this.getActivityGroupItemCount(group) > 1;
  }

  canCollapseActivityTableGroup(group: CurrentActivityGroup, groups: CurrentActivityGroup[]): boolean {
    return groups.length > 1 && this.canCollapseActivityGroup(group);
  }

  isActivityTableGroupExpanded(group: CurrentActivityGroup, groups: CurrentActivityGroup[]): boolean {
    return !this.canCollapseActivityTableGroup(group, groups) ||
      this.expandedActivityTableGroups.has(this.getActivityGroupKey(group));
  }

  toggleActivityTableGroup(group: CurrentActivityGroup, groups: CurrentActivityGroup[]): void {
    if (!this.canCollapseActivityTableGroup(group, groups)) {
      return;
    }

    const key = this.getActivityGroupKey(group);

    if (this.expandedActivityTableGroups.has(key)) {
      this.expandedActivityTableGroups.delete(key);
      return;
    }

    this.expandedActivityTableGroups.add(key);
  }

  isTextTruncated(element: HTMLElement | null): boolean {
    if (!element) {
      return false;
    }

    return element.scrollWidth > element.clientWidth || element.scrollHeight > element.clientHeight;
  }

  private initializeViewModel(): Observable<PersonPageViewModel | null> {
    return this.route.paramMap.pipe(
      switchMap(paramMap => {
        this.personId = paramMap.get('person-id')!;
        this.trustedMembersPageIndex = 0;
        return this.reload$.pipe(
          startWith(void 0),
          switchMap(() => {
            this.loadError = null;
            return this.loadPersonData(this.personId).pipe(
              catchError(error => {
                this.loadError = error instanceof HttpErrorResponse && error.status === 403
                  ? 'No tenés permiso para ver la ficha de este miembro.'
                  : 'No se pudo cargar la ficha del miembro. Intentá nuevamente.';
                return of(null);
              })
            );
          })
        );
      }),
      takeUntil(this.destroy$)
    );
  }

  private loadPersonData(personId: string): Observable<PersonPageViewModel> {
    return this.personService.getPerson(personId).pipe(
      switchMap(person => {
        const today = new Date();
        const from = new Date(today);
        from.setDate(from.getDate() - 28);
        const to = new Date(today);
        to.setDate(to.getDate() + 1);
        const formatDate = (date: Date) => `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
        return combineLatest([
        of(person),
        this.personEventService.getEvents(personId).pipe(catchError(() => of([] as PersonEvent[]))),
        this.personService.getCommunityHistory(personId).pipe(catchError(() => of([] as PersonCommunityHistoryItem[]))),
        this.personService.getDivineServiceAttendance(personId, 100).pipe(catchError(() => of([] as PersonDivineServiceAttendance[]))),
        this.divineServiceApi.getDivineServices(person.communityId, formatDate(from), formatDate(to), 'startAt', 'desc', 1, 100)
          .pipe(map(response => [...(response.items ?? [])].reverse()), catchError(() => of([] as DivineServiceListDto[]))),
        this.personService.getAuthorizedUnits().pipe(catchError(() => of([] as OrganizationUnitItem[]))),
        this.personService.getFamilyRelationships(personId).pipe(catchError(() => of({ parents: [], children: [] }))),
        this.personService.getTrustedMembers(personId).pipe(catchError(() => of([] as ResponsiblePriestMember[]))),
        this.spiritualAssistanceVisitApi.list(undefined, undefined, undefined, personId).pipe(catchError(() => of([] as SpiritualAssistanceVisit[])))
      ]);
      }),
      switchMap(([person, events, communityHistory, divineServiceAttendance, recentDivineServices, units, familyRelationships, trustedMembers, spiritualAssistanceVisits]) => loadCommunityHierarchy(person.communityId, units, this.organizationUnitApi).pipe(map(communityAncestors => {
        this.breadcrumbService.setDynamicCrumb('unit-id', person.communityName);
        this.breadcrumbService.setDynamicCrumb('person-id', `${person.firstName} ${person.lastName}`);
        return {
          person,
          age: this.calculateAge(person.dateOfBirth),
          activity: this.resolveActivity(person, events, units),
          events: this.sortEvents(events),
          communityHistory,
          communityAncestors,
          divineServiceAttendance,
          recentDivineServices,
          familyRelationships,
          trustedMembers,
          spiritualAssistanceVisits: [...spiritualAssistanceVisits].sort((a, b) => b.visitDate.localeCompare(a.visitDate))
        } as PersonPageViewModel;
      })))
    );
  }

  getLastSpiritualAssistanceVisit(vm: PersonPageViewModel): SpiritualAssistanceVisit | undefined {
    return vm.spiritualAssistanceVisits[0];
  }

  getPreviousSpiritualAssistanceVisits(vm: PersonPageViewModel): SpiritualAssistanceVisit[] {
    return vm.spiritualAssistanceVisits.slice(1);
  }

  getLastDivineServiceAttendance(vm: PersonPageViewModel): PersonDivineServiceAttendance | undefined {
    return vm.divineServiceAttendance[0];
  }

  getAttendedDivineServiceCount(vm: PersonPageViewModel): number {
    const attendedIds = new Set(vm.divineServiceAttendance.map(item => item.divineServiceId));
    return vm.recentDivineServices.filter(service => attendedIds.has(service.id!)).length;
  }

  isPersonPresentAtDivineService(vm: PersonPageViewModel, service: DivineServiceListDto): boolean {
    return vm.divineServiceAttendance.some(item => item.divineServiceId === service.id);
  }

  getSelectedDivineService(vm: PersonPageViewModel): DivineServiceListDto | undefined {
    const selected = vm.recentDivineServices.find(service => service.id === this.selectedDivineServiceId);
    return selected ?? vm.recentDivineServices.at(-1);
  }

  selectDivineService(serviceId: string | undefined): void {
    this.selectedDivineServiceId = serviceId ?? '';
  }

  getDivineServiceDateLabel(date: string | Date | undefined, options: Intl.DateTimeFormatOptions = { day: 'numeric', month: 'short', year: 'numeric' }): string {
    if (!date) return '';
    return new Date(date).toLocaleDateString('es-AR', options);
  }

  getDivineServiceRangeLabel(vm: PersonPageViewModel): string {
    if (!vm.recentDivineServices.length) return '';
    const dates = vm.recentDivineServices.map(item => new Date(item.startAt!).getTime()).filter(Number.isFinite);
    if (!dates.length) return '';
    const oldest = new Date(Math.min(...dates));
    const newest = new Date(Math.max(...dates));
    const format = (date: Date) => date.toLocaleDateString('es-AR', { day: 'numeric', month: 'short' });
    return `${format(oldest)} - ${format(newest)}`;
  }

  getAttendancePercentage(vm: PersonPageViewModel): number {
    return vm.recentDivineServices.length
      ? Math.round(this.getAttendedDivineServiceCount(vm) / vm.recentDivineServices.length * 100)
      : 0;
  }

  getVisitLocation(visit: SpiritualAssistanceVisit): string {
    if (visit.locationType === 'Home') return 'Hogar';
    if (visit.locationType === 'Community') return 'Comunidad';
    return visit.locationDescription || 'Otro lugar';
  }

  getVisitDateLabel(visit: SpiritualAssistanceVisit): string {
    return new Date(`${visit.visitDate}T00:00:00`).toLocaleDateString('es-AR', { weekday: 'short', day: '2-digit', month: '2-digit', year: 'numeric' });
  }

  getVisitDaysAgoLabel(visit: SpiritualAssistanceVisit): string {
    const days = Math.max(0, Math.floor((new Date().setHours(0, 0, 0, 0) - new Date(`${visit.visitDate}T00:00:00`).getTime()) / 86400000));
    return days === 0 ? 'Hoy' : days === 1 ? 'Hace 1 día' : `Hace ${days} días`;
  }

  getAttendanceDaysAgoLabel(item: PersonDivineServiceAttendance): string {
    const days = Math.max(0, Math.floor((Date.now() - new Date(item.startAt).getTime()) / 86400000));
    return days === 0 ? 'Hoy' : days === 1 ? 'Hace 1 día' : `Hace ${days} días`;
  }

  shouldShowTrustedMemberCommunity(person: Person): boolean {
    const activeUnitIds = new Set(
      person.ministry?.activities
        .filter(activity => !activity.validTo && !!activity.unitId)
        .map(activity => activity.unitId) ?? []
    );
    return activeUnitIds.size > 1;
  }

  private calculateAge(dateOfBirth: Date | undefined): number | undefined {
    if (!dateOfBirth || isNaN(dateOfBirth.getTime())) {
      return undefined;
    }

    const today = new Date();
    let age = today.getFullYear() - dateOfBirth.getFullYear();
    const monthDiff = today.getMonth() - dateOfBirth.getMonth();

    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < dateOfBirth.getDate())) {
      age--;
    }

    return Math.max(0, age);
  }

  private resolveActivity(person: Person, events: PersonEvent[], units: OrganizationUnitItem[]): PersonPageViewModel['activity'] {
    const unitLevels = new Map(units.map(unit => [unit.id, unit.levelId]));
    const items: CurrentActivityItem[] = [
      ...(this.hasCurrentMinistry(person)
        ? [{
            name: person.ministry.ministryName,
            unitId: person.ministry.unitId,
            unitName: person.ministry.unitName,
            unitLevelId: this.resolveUnitLevelId(person.ministry.unitId, unitLevels),
            statusLabel: this.getMinistryStatusLabel(person.ministry.status),
            ministryStatus: person.ministry.status as MinistryStatus,
            ministryActivityUnits: [...new Map(
              person.ministry.activities
                .filter(activity => !!activity.unitName)
                .map(activity => [activity.unitId || activity.unitName, { id: activity.unitId, name: activity.unitName }] as const)
            ).values()],
            typeLabel: 'Ministerio'
          }]
        : []),
      ...this.getActiveEvents(events, 'appointment').map(event => this.mapEventToActivityItem(event, 'Encargo', unitLevels)),
      ...this.getActiveEvents(events, 'assignment').map(event => this.mapEventToActivityItem(event, 'Nombramiento', unitLevels)),
      ...events
        .filter(event =>
          event.category === 'Collaboration' &&
          event.eventKind !== 'appointment' &&
          event.eventKind !== 'assignment' &&
          this.isCurrentEvent(event)
        )
        .map(event => this.mapEventToActivityItem(event, 'Colaboracion', unitLevels))
    ];

    const categories = ['Ministerio', 'Encargos', 'Nombramientos', 'Colaboraciones']
      .map(label => ({
        label,
        items: items.filter(item => this.getActivityCategoryLabel(item.typeLabel) === label)
      }))
      .filter(category => category.items.length > 0);

    return { groups: this.groupActivityByUnit(items), categories };
  }

  private hasCurrentMinistry(person: Person): person is Person & { ministry: NonNullable<Person['ministry']> } {
    return !!person.ministry && person.ministry.status !== 'Terminated';
  }

  private getActiveEvents(events: PersonEvent[], eventKind: string): PersonEvent[] {
    return events.filter(event => event.eventKind === eventKind && this.isCurrentEvent(event));
  }

  private isCurrentEvent(event: PersonEvent): boolean {
    if (!event.endDate) {
      return true;
    }

    const [year, month, day] = event.endDate.split('-').map(Number);
    const endDate = new Date(year, month - 1, day);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return endDate >= today;
  }

  private mapEventToActivityItem(
    event: PersonEvent,
    typeLabel: string,
    unitLevels: Map<string, number>
  ): CurrentActivityItem {
    return {
      name: event.eventTypeName,
      unitId: event.organizationUnitId,
      unitName: event.organizationUnitName,
      unitLevelId: this.resolveUnitLevelId(event.organizationUnitId, unitLevels),
      detail: typeLabel === 'Colaboracion' ? event.detail : undefined,
      requiresActiveMinistry: event.eventKind === 'appointment' || event.requiresActiveMinistry,
      typeLabel
    };
  }

  private groupActivityByUnit(items: CurrentActivityItem[]): CurrentActivityGroup[] {
    const groups = new Map<string, CurrentActivityGroup>();

    for (const item of items) {
      const key = item.unitId || item.unitName || 'without-unit';
      const group = groups.get(key) ?? {
        unitId: item.unitId,
        unitName: item.unitName || 'Sin unidad',
        unitLevelId: item.unitLevelId ?? Number.MAX_SAFE_INTEGER,
        categories: []
      };
      const categoryLabel = this.getActivityCategoryLabel(item.typeLabel);
      let category = group.categories.find(current => current.label === categoryLabel);

      if (!category) {
        category = { label: categoryLabel, items: [] };
        group.categories.push(category);
      }

      category.items.push(item);
      groups.set(key, group);
    }

    return [...groups.values()].sort((a, b) =>
      a.unitLevelId - b.unitLevelId ||
      a.unitName.localeCompare(b.unitName, 'es', { sensitivity: 'base' })
    );
  }

  private resolveUnitLevelId(unitId: string | undefined, unitLevels: Map<string, number>): number {
    return unitId ? unitLevels.get(unitId) ?? Number.MAX_SAFE_INTEGER : Number.MAX_SAFE_INTEGER;
  }

  private getActivityCategoryLabel(typeLabel: string): string {
    const labels: Record<string, string> = {
      Ministerio: 'Ministerio',
      Encargo: 'Encargos',
      Nombramiento: 'Nombramientos',
      Colaboracion: 'Colaboraciones',
    };
    return labels[typeLabel] ?? typeLabel;
  }

  private getMinistryStatusLabel(status: string): string {
    const map: Record<string, string> = {
      Active: 'Activo',
      Licensed: 'De licencia',
      Retired: 'En descanso',
      Terminated: 'Terminado',
    };
    return map[status] ?? status;
  }

  private sortEvents(events: PersonEvent[]): PersonEvent[] {
    return [...events].sort((a, b) => (b.eventDate ?? '').localeCompare(a.eventDate ?? ''));
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
